import React, { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { Button, Col, Row } from "reactstrap"
import axios from "axios"
import { api } from "../../utils/variables"
import TopAppCardM from "../MetaInsight/TopAppCardM"
import "./trending.css"
import TopAppCardSkeleton from "./TopAppCardSkeleton"
import FilterPanel from "../../components/KeywordTrackingNewUIPage/FilterPanel"
import { useFilters } from "../../hooks/useFilters"
import { useUrlPlatform } from "../../hooks/useUrlPlatform"
import InsightsPageHeader from "../../components/MetaInsight/InsightsPageHeader"
import SEO from "../../components/Common/SEO"
import { PAGE_SEO } from "../../config/seoConfig"
import { generateCanonicalUrl, generateItemListStructuredData, generateSlug, applySEODate } from "../../utils/seoUtils"
import PlatformsApi from "../../services/platformsApi"
import { useAnalytics, ANALYTICS_EVENTS } from "../../analytics"

export default function TrendingPage() {
  const { filterSections } = useFilters()
  const { trackEvent } = useAnalytics()
  const location = useLocation()
  const navigate = useNavigate()
  // Only set when reached via a Weekly Insights "View All" click — landing here
  // directly (sidebar link, bookmark, etc.) leaves this unset, so no back button shows.
  const cameFromWeeklyInsights = location.state?.from === 'weekly-insights'
  const [trendingApps, setTrendingApps] = useState([])
  const [trendingCountry, setTrendingCountry] = useState(() => {
    try {
      return sessionStorage.getItem('trending_selectedCountry') || 'US'
    } catch (e) {
      return 'US'
    }
  })
  const [trendingLanguage, setTrendingLanguage] = useState(() => {
    try {
      return sessionStorage.getItem('trending_selectedLanguage')
    } catch (e) {
      return null
    }
  })
  const [trendingPerPage] = useState(8)
  const [trendingTotal, setTrendingTotal] = useState(0)
  const [trendingAll, setTrendingAll] = useState([])
  const [trendingAllCountry, setTrendingAllCountry] = useState(null)
  const [trendingAllLanguage, setTrendingAllLanguage] = useState(null)
  const [trendingAllPlatform, setTrendingAllPlatform] = useState(null)
  const [trendingLoading, setTrendingLoading] = useState(false)
  const [countriesList, setCountriesList] = useState([])
  const [countryOptions, setCountryOptions] = useState([])
  const [countriesLoading, setCountriesLoading] = useState(true)
  const [trendingLastUpdated, setTrendingLastUpdated] = useState(null)
  const [sortedTrendingApps, setSortedTrendingApps] = useState([])
  const [isSorted, setIsSorted] = useState(false)
  const [rankSort, setRankSort] = useState(null) // null | 'asc' | 'desc'
  const [itemsToShow, setItemsToShow] = useState(8)
  const [showAll, setShowAll] = useState(false)
  const [loading, setLoading] = useState(true)
  const [selectedCardId, setSelectedCardId] = useState(null)
  const [trendingFilterPanelOpen, setTrendingFilterPanelOpen] = useState(false)
  const [trendingAppliedFilters, setTrendingAppliedFilters] = useState({})
  const [trendingPage, setTrendingPage] = useState(1)
  const [platforms, setPlatforms] = useState([])
  // Lives in the `?platform=` URL query param (defaulting to Meta) rather than
  // plain state, so it survives a round trip to an app's detail page and back,
  // and so a direct link can pre-select a platform.
  const [selectedPlatform, setSelectedPlatform] = useUrlPlatform('meta')
  const [platformsLoading, setPlatformsLoading] = useState(true)

  const isTrendingMeta = sessionStorage.getItem("trendingMeta")

  useEffect(() => {
    // load countries/locales first, then trending
    const loadCountries = async () => {
      setCountriesLoading(true)
      setPlatformsLoading(true)
      let list = []
      try {
        const [countriesRes, fetchedPlatforms] = await Promise.all([
          axios.get(`${api}/countries/`),
          PlatformsApi.fetchPlatforms()
        ])
        const res = countriesRes  // keep rest of logic using `res`
        list = res.data?.data || []
        setCountriesList(list)
        setCountryOptions(list.map(c => ({ value: c.code, label: c.name })))
        setPlatforms(fetchedPlatforms || PlatformsApi.getDefaultPlatforms())
        setPlatformsLoading(false)

        // set default language for US if available
        const us = list.find(x => x.code === 'US')
        const defaultLocale = us && Array.isArray(us.locales) && us.locales.length > 0 ? us.locales[0] : null
        setTrendingLanguage(prev => prev || defaultLocale)
      } catch (err) {
        console.error('Error loading countries:', err)
        setPlatforms(PlatformsApi.getDefaultPlatforms())
        setPlatformsLoading(false)
      } finally {
        setCountriesLoading(false)
        // compute initial country/language to use for the first fetch so behavior
        // matches the dashboard: prefer existing state, otherwise use loaded list
        const initCountry = trendingCountry || (list && list.length > 0 ? list[0].code : 'US')
        const countryEntry = list.find(x => x.code === initCountry)
        const initLangFromList = countryEntry && Array.isArray(countryEntry.locales) && countryEntry.locales.length > 0 ? countryEntry.locales[0] : null
        const initLanguage = trendingLanguage || initLangFromList || null
        setTrendingCountry(prev => prev || initCountry)
        setTrendingLanguage(prev => prev || initLanguage)

        // restore saved filters (persisted across route changes)
        let savedFilters = null
        try {
          const raw = sessionStorage.getItem('trendingAppliedFilters')
          savedFilters = raw ? JSON.parse(raw) : null
        } catch (err) {
          savedFilters = null
        }
        setTrendingAppliedFilters(savedFilters || {})

        setLoading(true)
        // pass savedFilters explicitly so initial fetch uses the persisted filters
        fetchTrendingApps(initCountry, initLanguage, 1, savedFilters || {})
      }
    }

    loadCountries()
  }, [])

  const handlePlatformChange = (platformId) => {
    setSelectedPlatform(platformId)
    setTrendingAll([])
    fetchTrendingApps(trendingCountry, trendingLanguage, 1, trendingAppliedFilters, platformId)
  }

  const fetchTrendingApps = async (country = trendingCountry, language = trendingLanguage, page = trendingPage, filters = trendingAppliedFilters, platform = selectedPlatform) => {
    try {
      console.info('fetchTrendingApps called', { country, language, page, filters })

      // If we already have full data and filters haven't changed, paginate locally instead of re-fetching
      if (
        trendingAll && trendingAll.length > 0 &&
        JSON.stringify(filters) === JSON.stringify(trendingAppliedFilters) &&
        country === trendingAllCountry &&
        language === trendingAllLanguage &&
        platform === trendingAllPlatform
      ) {
        const start = (page - 1) * trendingPerPage
        setTrendingApps(trendingAll.slice(start, start + trendingPerPage))
        setTrendingPage(page)
        return
      }

      setTrendingLoading(true)
      const params = {}
      if (country) params.country = country
      if (language) params.language = language
      // Add filter params
      if (filters && typeof filters === 'object') {
        if (filters.price) params.price = Array.isArray(filters.price) ? filters.price.join(',') : filters.price
        if (filters.rating) params.rating = Array.isArray(filters.rating) ? filters.rating.join(',') : filters.rating
        if (filters.genre) params.genre = Array.isArray(filters.genre) ? filters.genre.join(',') : filters.genre
        // Accept either `sort_by` (preferred) or legacy `sort` and normalize to sort_by
        if (filters.sort_by) params.sort_by = filters.sort_by
        else if (filters.sort) params.sort_by = filters.sort
        if (filters.bundle) params.bundle = Array.isArray(filters.bundle) ? filters.bundle.join(',') : filters.bundle
        if (filters.number_of_players) params.number_of_players = filters.number_of_players
      }

      // Fetch only Meta trending
      const token = localStorage.getItem("authToken")
      const headers = {}
      if (token) headers.Authorization = `Bearer ${token}`

      const resMeta = await axios.get(`${api}/tracker/stats/${platform}/trending`, { params, headers })
      const combinedTrendingApps = [...(resMeta.data?.games || [])]

      setTrendingTotal(combinedTrendingApps.length)

      // Client-side pagination
      const startIndex = (page - 1) * trendingPerPage
      const endIndex = startIndex + trendingPerPage
      const paginatedApps = combinedTrendingApps.slice(startIndex, endIndex)

      sessionStorage.setItem("trendingMeta", JSON.stringify(combinedTrendingApps))
      setTrendingAll(combinedTrendingApps)
      setTrendingAllCountry(country)
      setTrendingAllLanguage(language)
      setTrendingAllPlatform(platform)
      setTrendingApps(paginatedApps)
      setTrendingLastUpdated(new Date())
      trackEvent(ANALYTICS_EVENTS.FEATURE.WEEKLY_INSIGHT_VIEWED, {
        insight_type: ANALYTICS_EVENTS.WEEKLY_INSIGHT_TYPES.TRENDING,
        platform,
        country,
      })
    } catch (error) {
      console.error("Error fetching trending apps:", error)
    }
    finally {
      setTrendingLoading(false)
      setLoading(false)
    }
  }

  // sorting removed: control was intentionally removed from the UI

  useEffect(() => {
    if (showAll) setItemsToShow(trendingAll.length)
    else setItemsToShow(8)
  }, [showAll, trendingAll.length])

  const displayApps = isSorted
    ? sortedTrendingApps.slice(0, itemsToShow)
    : (showAll ? trendingAll.slice(0, itemsToShow) : trendingApps.slice(0, itemsToShow))

  // Helper to parse rank from API item
  const parseRank = (app) => {
    if (!app) return Number.MAX_SAFE_INTEGER
    const candidates = [app.rank, app.position, app.ranking, app.rank_position, app.rankValue]
    for (let c of candidates) {
      if (c !== undefined && c !== null && c !== '' && !isNaN(Number(c))) return Number(c)
    }
    // fallback: try nested fields
    if (app.meta && (app.meta.rank !== undefined)) return Number(app.meta.rank)
    return Number.MAX_SAFE_INTEGER
  }

  const sortByRank = (order = 'asc') => {
    const arr = Array.isArray(trendingAll) ? [...trendingAll] : []
    arr.sort((a, b) => {
      const ra = parseRank(a)
      const rb = parseRank(b)
      return order === 'asc' ? (ra - rb) : (rb - ra)
    })
    setSortedTrendingApps(arr)
    setIsSorted(true)
  }

  const toggleRankSort = () => {
    const next = rankSort === 'asc' ? 'desc' : 'asc'
    setRankSort(next)
    sortByRank(next)
  }

  // Re-apply sorting when the underlying data changes or sort order changes
  useEffect(() => {
    if (!rankSort) {
      setIsSorted(false)
      setSortedTrendingApps([])
      return
    }
    sortByRank(rankSort)
  }, [trendingAll, rankSort])

  // Country/language select rendering (flags, react-select styling) now lives in
  // InsightsPageHeader; this page only needs to resolve a language when the country
  // changes, so its fetch call gets the right locale.
  const handleTrendingCountryChange = (code, langForCountry) => {
    setTrendingCountry(code)
    setTrendingLanguage(langForCountry)
    try {
      sessionStorage.setItem('trending_selectedCountry', code)
      sessionStorage.setItem('trending_selectedLanguage', langForCountry || '')
    } catch (e) { /* ignore */ }
    setLoading(true)
    fetchTrendingApps(code, langForCountry)
  }

  const handleTrendingLanguageChange = (val) => {
    setTrendingLanguage(val)
    try {
      sessionStorage.setItem('trending_selectedLanguage', val || '')
    } catch (e) { /* ignore */ }
    setLoading(true)
    fetchTrendingApps(trendingCountry, val)
  }

  const handleCardClick = (cardId) => {
    setSelectedCardId(cardId)
  }

  const handleTrendingFilterApply = (filters) => {
    // Normalize filters: convert 'sort' to 'sort_by' (FilterPanel uses 'sort' as section id)
    const normalizedFilters = { ...(filters || {}) }
    // Accept either `sort_by` (preferred) or legacy `sort` and normalize to sort_by
    if (normalizedFilters.sort_by) {
      // Already has sort_by, keep it
    } else if (normalizedFilters.sort) {
      normalizedFilters.sort_by = normalizedFilters.sort
      delete normalizedFilters.sort
    }

    setTrendingAppliedFilters(normalizedFilters)
    setTrendingPage(1)
    setLoading(true)
    setTrendingLoading(true)
    // persist filters so they survive route changes
    try { sessionStorage.setItem('trendingAppliedFilters', JSON.stringify(normalizedFilters || {})) } catch (e) { /* ignore */ }
    fetchTrendingApps(trendingCountry, trendingLanguage, 1, normalizedFilters)
    setTrendingFilterPanelOpen(false)
  }

  const handleRemoveFilter = (key) => {
    const newFilters = { ...(trendingAppliedFilters || {}) }
    if (Object.prototype.hasOwnProperty.call(newFilters, key)) {
      delete newFilters[key]
      setTrendingAppliedFilters(newFilters)
      setTrendingPage(1)
      // re-fetch with the updated filters
      setLoading(true)
      setTrendingLoading(true)
      try { sessionStorage.setItem('trendingAppliedFilters', JSON.stringify(newFilters || {})) } catch (e) { /* ignore */ }
      fetchTrendingApps(trendingCountry, trendingLanguage, 1, newFilters)
    }
  }

  // Generate structured data for trending apps
  const trendingStructuredData = generateItemListStructuredData(
    'Trending VR Apps 2025',
    displayApps,
    (app) => ({
      '@type': 'SoftwareApplication',
      name: app?.name || 'VR App',
      url: `${window.location.origin}/xr/app/${generateSlug(app?.name || '')}?id=${app?.item_id}`,
      applicationCategory: 'GameApplication',
      operatingSystem: 'Meta Quest',
    })
  );

  // Description/disclaimer reference whichever store is currently selected, rather
  // than always saying "Meta" regardless of the active platform tab.
  const platformLabel = PlatformsApi.getPlatformDisplayName(selectedPlatform, platforms)
  const platformIconUrl = PlatformsApi.getPlatformIcon(selectedPlatform, platforms)

  return (
    <React.Fragment>
      <SEO
        title={applySEODate(PAGE_SEO.trending.title)}
        description={PAGE_SEO.trending.description}
        keywords={PAGE_SEO.trending.keywords}
        canonical={generateCanonicalUrl('/trending')}
        robots={PAGE_SEO.trending.robots}
        structuredData={trendingStructuredData}
      />
      <div className="container-fluid " style={{ maxWidth: "100vw", padding: 32 }}>
        <div className="category_track_innersec">
          <div className="row">
            <div className="col-sm-12">
              <InsightsPageHeader
                onBack={cameFromWeeklyInsights ? () => navigate('/weekly-insights') : undefined}
                title="Top Trending"
                description={`Discover the most trending apps of the week on the ${platformLabel} Store — your ${platformLabel} weekly insights at your fingertips. Use different filters to explore performance across all available apps.`}
                disclaimer={`Insights are based on ${platformLabel} Store rankings and KeyQuest's tracking metrics.`}
                platforms={platforms}
                platformsLoading={platformsLoading}
                selectedPlatform={selectedPlatform}
                onPlatformChange={handlePlatformChange}
                countriesList={countriesList}
                countryOptions={countryOptions}
                countriesLoading={countriesLoading}
                selectedCountry={trendingCountry}
                selectedLanguage={trendingLanguage}
                onCountryChange={handleTrendingCountryChange}
                onLanguageChange={handleTrendingLanguageChange}
                appliedFilters={trendingAppliedFilters}
                onRemoveFilter={handleRemoveFilter}
                lastUpdated={trendingLastUpdated}
                onOpenFilters={() => setTrendingFilterPanelOpen(true)}
                rankSort={rankSort}
                onToggleSort={toggleRankSort}
              />
            </div>
          </div>
          {/* Header Bar */}


          {/* Content Area */}
          <div className="trending-content">
            {/* App Grid */}
            {(loading || trendingLoading) ? (
              <Row className="g-3">
                {Array.from({ length: 4 }).map((_, index) => (
                  <Col key={index} xs={12} sm={6}>
                    <div className="trending-card">
                      <TopAppCardSkeleton />
                    </div>
                  </Col>
                ))}
              </Row>
            ) : (
              <>
                {displayApps.length > 0 ? (
                  <Row className="">
                    {displayApps.map((app, index) => (
                      <Col key={app?.item_id || index} xs={12} sm={6}>
                        <div
                          className={`trending-card ${selectedCardId === app?.item_id ? 'selected' : ''}`}
                          onClick={() => handleCardClick(app?.item_id)}
                        >
                          <TopAppCardM
                            gamedata={app}
                            priceLayout="discounts"
                            showEnds={false}
                            headingLevel="h2"
                            insightType={ANALYTICS_EVENTS.WEEKLY_INSIGHT_TYPES.TRENDING}
                            sourceSurface="trending_page"
                            platformIcon={platformIconUrl}
                            platformName={platformLabel}
                          />
                        </div>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <div style={{ textAlign: 'center', padding: 40 }}>
                    <p style={{ color: '#A1ACB8', fontSize: 16 }}>
                      {Object.keys(trendingAppliedFilters || {}).length > 0
                        ? 'No results matched with filter.'
                        : 'No results to display.'}
                    </p>
                  </div>
                )}

                {/* See More/Less Button */}
                {!loading && (
                  <div className="text-center">
                    {!showAll && trendingAll.length > 8 ? (
                      <Button
                        className="see-more-btn"
                        onClick={() => setShowAll(true)}
                      >
                        See More
                      </Button>
                    ) : showAll ? (
                      <Button
                        className="see-less-btn"
                        onClick={() => setShowAll(false)}
                      >
                        See Less
                      </Button>
                    ) : null}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <FilterPanel
        open={trendingFilterPanelOpen}
        sections={filterSections.length > 0 ? filterSections : undefined}
        // Platform is already selectable via the Meta Quest/Steam tabs above -
        // don't duplicate it inside the filter panel.
        hidePlatformSection
        onClose={() => setTrendingFilterPanelOpen(false)}
        onApply={handleTrendingFilterApply}
        initialValues={(() => {
          // Convert sort_by back to sort for FilterPanel (FilterPanel uses 'sort' as section id)
          const panelFilters = { ...(trendingAppliedFilters || {}) }
          if (panelFilters.sort_by && !panelFilters.sort) {
            panelFilters.sort = panelFilters.sort_by
            delete panelFilters.sort_by
          }
          return panelFilters
        })()}
      />
    </React.Fragment>
  )
}